import { useEffect, useState } from "react";

/**
 * Reads the large-display scale factor (`--ui-scale`, defined in index.css).
 *
 * CSS owns the breakpoints so that stylesheets and components can never disagree about how big
 * the UI currently is. Use this only for sizes that must be numbers in JS — a canvas's width, an
 * SVG radius, an avatar's pixel size. Anything expressible in CSS should use `rem` (scales with
 * the root font size) or `calc(var(--ui-scale) * …)` instead.
 */
export function readUiScale(): number {
  if (typeof window === "undefined" || typeof getComputedStyle !== "function") return 1;
  const raw = getComputedStyle(document.documentElement).getPropertyValue("--ui-scale");
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

export default function useUiScale(): number {
  const [scale, setScale] = useState(readUiScale);

  useEffect(() => {
    const update = () => setScale(readUiScale());
    // The var is resolved from the stylesheet, which may land after first paint.
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return scale;
}

/** Scale a design pixel value authored against 1920×1080. */
export function useScaledPx(px: number): number {
  return Math.round(useUiScale() * px);
}

/**
 * Extra shrink factor for the tall result panels on short viewports.
 *
 * `--ui-scale` only ever grows the UI (a 1440p screen should not show a 1080p-sized island). The
 * opposite case exists too: a browser window shorter than a full 1080p viewport — a small window,
 * page zoom, a laptop — leaves less room than the panels were authored for, and the versus panels
 * are the tallest thing in the game. This returns ≤ 1 so they shrink to fit instead of overflowing.
 *
 * @param naturalPx  the panel's own height at scale 1
 * @param reservedPx everything else competing for the viewport (header, padding, buttons)
 */
export function useFitScale(naturalPx: number, reservedPx: number, min = 0.7): number {
  const [vh, setVh] = useState(() => (typeof window === "undefined" ? 1080 : window.innerHeight));

  useEffect(() => {
    const update = () => setVh(window.innerHeight);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  if (naturalPx <= 0) return 1;
  return Math.min(1, Math.max(min, (vh - reservedPx) / naturalPx));
}
